import { useTranslate } from '../../application/i18n/LocaleProvider';
import { BrandWordmark } from '../chrome/BrandMark';

/**
 * The back of a card, and the two things a deck does with backs
 * (SPEC-011 PR-11.5).
 *
 * A back is the SAME PAPER as a face. That is the whole design of it: printing
 * the reverse dark would make a face-down card a different material from a
 * face-up one, and a hand you are looking at the back of is the same five cards
 * you are about to be shown. Cream stock, the printed double frame, and the
 * wordmark centred — which is what the physical decks do and what the reader
 * already recognises from the header.
 *
 * The wordmark is the brand's raster lockup rather than type, for the reason
 * `BrandMark` gives: §04 calls a single-colour rendering of it a dead lockup, so
 * there is no text version to reach for.
 *
 * NOTHING HERE IS INTERACTIVE. A face-down card cannot be chosen — the
 * opponent's hand is theirs and the stock is not dealt by tapping it — so these
 * are `<span>`s and never buttons. That is also why the counts beside them are
 * drawn: a fan of five backs and a fan of four are hard to tell apart at a
 * glance, and the number is the fact.
 */

/** One back. `index` and `count` place it in a fan; alone it is neither. */
export function OvertakeCardBack({ index = 0, count = 1, className = '' }) {
    // The signed distance from the middle of the fan, in card-widths: -2, -1, 0,
    // 1, 2 for five. The stylesheet turns it into a rotation and a lift, so the
    // arithmetic is here and the units are there — the split `DuelOption` uses
    // for `--duel-side`.
    const turn = index - (count - 1) / 2;

    return (
        <span
            className={`overtake-back ${className}`.trim()}
            style={{ '--fan-turn': turn, '--fan-index': index }}
        >
            <BrandWordmark
                className="overtake-back__mark"
                /* Decorative: the fan and the stack both carry a counted,
                   worded label, and a wordmark announced once per card would
                   read the product's name five times into a hand. */
                alt=""
                sizes="72px"
            />
        </span>
    );
}

/**
 * The opponent's hand: what they hold, without what they hold.
 *
 * Information the local game did not previously show at all. How many cards are
 * left in the other hand is the single most useful thing a player can know that
 * is not secret — it is the difference between an opponent who can still answer
 * and one who is about to pass — and every physical version of this game gives
 * it away by simply being visible across the table.
 */
export function OvertakeBackFan({ count = 0, label }) {
    const backs = Math.max(0, count);

    return (
        <div className="overtake-rival-hand">
            <div className="overtake-back-fan" aria-hidden="true">
                {Array.from({ length: backs }, (unused, index) => (
                    <OvertakeCardBack
                        /* Positional on purpose: these carry no identity, and
                           there is nothing else to key them by. */
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        index={index}
                        count={backs}
                    />
                ))}
            </div>
            <p className="overtake-count">
                <span className="overtake-count__value figure">{backs}</span>
                <span className="overtake-count__label">{label}</span>
            </p>
        </div>
    );
}

/**
 * The stock: the cards nobody has been dealt yet.
 *
 * Drawn as a stack whose DEPTH is the count rather than as one back with a
 * number on it, because the stock emptying is the clock of the whole match —
 * hands stop refilling, and then they run out. A pile that visibly thins is that
 * clock; a numeral is a fact you have to read.
 *
 * The layers are capped: past about six the difference between eight and nine
 * edges is not perceivable, and the number beside it is exact anyway.
 */
const MAX_VISIBLE_LAYERS = 6;

export function OvertakeBackStack({ count = 0, label }) {
    const t = useTranslate();
    const layers = Math.min(Math.max(0, count), MAX_VISIBLE_LAYERS);

    return (
        <div className={`overtake-stock${count > 0 ? '' : ' overtake-stock--spent'}`}>
            <div className="overtake-back-stack" aria-hidden="true">
                {Array.from({ length: layers }, (unused, index) => (
                    <OvertakeCardBack
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        index={index}
                        count={layers}
                        className="overtake-back--stacked"
                    />
                ))}
                {count > 0 ? null : (
                    <span className="overtake-stock__empty">{t('play.stock.empty')}</span>
                )}
            </div>

            {/* NAMED AND COUNTED, in the shape the table's three columns share:
                a pile is only legible beside the two others if all three say
                what they are the same way. The sentence this used to carry —
                "two in the stock" — said the same thing in a form that could not
                sit under a card without wrapping. */}
            <p className="overtake-stock__caption">
                <span className="overtake-stock__label">{label ?? t('play.table.stock')}</span>
                <span className="overtake-stock__count figure">{Math.max(0, count)}</span>
            </p>
        </div>
    );
}
